import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { fetchProducts, fetchShopInfo, fetchCollections } from '../services/shopifyClient';

const ShopifyContext = createContext(null);

// Auto-refresh interval (5 minutes in milliseconds)
const AUTO_REFRESH_INTERVAL = 5 * 60 * 1000;

export const ShopifyProvider = ({ children }) => {
  const [products, setProducts] = useState([]);
  const [shop, setShop] = useState(null);
  const [collections, setCollections] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [lastUpdated, setLastUpdated] = useState(null);
  const refreshIntervalRef = useRef(null);

  const loadData = useCallback(async (showLoading = true) => {
    if (showLoading) {
      setLoading(true);
    }
    setError(null);

    try {
      const [productsData, shopData, collectionsData] = await Promise.all([
        fetchProducts(100),
        fetchShopInfo(),
        fetchCollections(50),
      ]);

      // Build categories from collections first, then product types
      let categoryList = [];
      
      // If we have collections from Shopify, use them
      if (collectionsData && collectionsData.length > 0) {
        categoryList = collectionsData.map(c => ({
          id: c.id,
          title: c.title,
          handle: c.handle,
          type: 'collection',
          productCount: c.productCount,
        }));
        
        // Add collection associations to products
        const productsWithCollections = productsData.products.map(product => {
          const productCollections = collectionsData
            .filter(collection => collection.productIds.includes(product.id))
            .map(c => c.handle);
          
          return {
            ...product,
            categoryHandles: productCollections,
          };
        });
        
        setProducts(productsWithCollections);
      } else {
        // Fallback: Use product types as categories
        const productTypeMap = new Map();
        
        productsData.products.forEach(product => {
          if (product.productType && product.productType.trim()) {
            const type = product.productType.trim();
            if (!productTypeMap.has(type)) {
              productTypeMap.set(type, {
                id: `type-${type.toLowerCase().replace(/\s+/g, '-')}`,
                title: type,
                handle: type.toLowerCase().replace(/\s+/g, '-'),
                type: 'productType',
                productCount: 0,
              });
            }
            productTypeMap.get(type).productCount++;
          }
        });
        
        categoryList = Array.from(productTypeMap.values())
          .filter(c => c.productCount > 0)
          .sort((a, b) => a.title.localeCompare(b.title));
        
        // Add category handles to products based on product type
        const productsWithCategories = productsData.products.map(product => {
          const categoryHandle = product.productType 
            ? product.productType.toLowerCase().replace(/\s+/g, '-')
            : null;
          
          return {
            ...product,
            categoryHandles: categoryHandle ? [categoryHandle] : [],
          };
        });
        
        setProducts(productsWithCategories);
      }
      
      setCollections(collectionsData);
      setCategories(categoryList);
      setShop(shopData);
      setLastUpdated(new Date());
      
      console.log(`[Shopify] Loaded ${productsData.products.length} products in ${categoryList.length} categories`);
    } catch (err) {
      console.error('Error loading Shopify data:', err);
      setError(err.message || 'Failed to load products');
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => {
    loadData();
  }, [loadData]);

  // Auto-refresh products to catch new additions
  useEffect(() => {
    refreshIntervalRef.current = setInterval(() => {
      console.log('[Shopify] Auto-refreshing products...');
      loadData(false);
    }, AUTO_REFRESH_INTERVAL);

    return () => {
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
    };
  }, [loadData]);

  // Refresh when tab becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && lastUpdated) {
        const timeSinceLastUpdate = Date.now() - lastUpdated.getTime();
        if (timeSinceLastUpdate > 60000) {
          console.log('[Shopify] Tab visible, refreshing products...');
          loadData(false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [loadData, lastUpdated]);

  // Filter products based on selected category
  const filteredProducts = selectedCategory === 'all' 
    ? products 
    : products.filter(product => 
        product.categoryHandles && product.categoryHandles.includes(selectedCategory)
      );

  const value = {
    products,
    filteredProducts,
    shop,
    collections,
    categories,
    loading,
    error,
    refetch: () => loadData(true),
    silentRefetch: () => loadData(false),
    selectedCategory,
    setSelectedCategory,
    lastUpdated,
    productCount: products.length,
    categoryCount: categories.length,
  };

  return (
    <ShopifyContext.Provider value={value}>
      {children}
    </ShopifyContext.Provider>
  );
};

export const useShopify = () => {
  const context = useContext(ShopifyContext);
  if (!context) {
    throw new Error('useShopify must be used within a ShopifyProvider');
  }
  return context;
};

export default ShopifyContext;
